import { useSelectedApPayee } from '@/components/ApPayee/useChangeApPayee';
import {
  ApplicationDetailHeader,
  ApplicationDetailRow,
} from '@/features/InvoiceReport/Edit/components/Drawer/OcrReflectionDrawer/Components/ApplicationDetailRow';
import { OcrInferenceResult } from '@/features/InvoiceReport/Edit/components/type';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { useTranslation } from '@/i18n';
import { getBankAccountTypeLabel } from '@/utils/bankAccountTypeLabel';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { FC, memo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  SelectedType,
  SelectionState,
} from '../Drawer/OcrReflectionDrawer/hooks/useApplicationFormSelectionManager';
import { getTransformedValue } from '../utils/ocrDataTransform';
import styles from './OcrReflectionApplicationForm.module.scss';
import { OcrReflectionApplicationFormSwitcher } from './OcrReflectionApplicationFormSwitcher';

type InferenceTooltipKey = 'bookDate' | 'dueDate' | 'invoiceRegistrationNumber';

const cx = classnames.bind(styles);

export type Props = {
  ocrInferenceResult?: OcrInferenceResult | null;
  invoiceRegistrationNumberKey?: string;
  isHiddenInvoiceRegistrationNumber?: boolean;
  draftInstantPayeeBankName?: string;
  draftInstantPayeeBankBranchName?: string;
  isInstantPayeeOpened: SelectedType;
  setIsInstantPayeeOpened: (type: SelectedType) => void;
  isVisibleInstantPayee: boolean;
  toggleAllChecked: () => void;
  allChecked: boolean;
  indeterminate: boolean;
  selectionState: SelectionState;
  updateSelection: (key: keyof SelectionState, value: boolean) => void;
};

const InnerOcrReflectionApplicationForm: FC<Props> = ({
  ocrInferenceResult,
  invoiceRegistrationNumberKey,
  isHiddenInvoiceRegistrationNumber,
  draftInstantPayeeBankName,
  draftInstantPayeeBankBranchName,
  isInstantPayeeOpened,
  setIsInstantPayeeOpened,
  isVisibleInstantPayee,
  toggleAllChecked,
  allChecked,
  indeterminate,
  selectionState,
  updateSelection,
}) => {
  const { t } = useTranslation();
  const formContext = useFormContext<PaymentRequestForm>();
  const formWatch = formContext.watch;
  const payee = useSelectedApPayee();

  // Helper function to convert getTransformedValue result to string
  const getTransformedValueAsString = useCallback(
    (key: keyof SelectionState): string => {
      const value = getTransformedValue(key, ocrInferenceResult);
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'string') {
        return value;
      }
      // Handle Dayjs case
      if (dayjs.isDayjs(value)) {
        return value.format('YYYY/MM/DD');
      }
      return String(value);
    },
    [ocrInferenceResult]
  );

  const getFormContextValues = useCallback(
    (name: string): string | undefined => {
      if (!formWatch) {
        return undefined;
      }
      let value: string | undefined;
      switch (name) {
        case 'payee':
          // formWatch('payee.value') is just id, so we need to get name from payee
          value = payee?.name;
          break;
        case 'book_date': {
          const bookDate = formWatch('payee.bookDate');
          value = bookDate ? dayjs(bookDate).format('YYYY/MM/DD') : undefined;
          break;
        }
        case 'due_date': {
          const dueDate = formWatch('payee.dueDate');
          value = dueDate ? dayjs(dueDate).format('YYYY/MM/DD') : undefined;
          break;
        }
        case 'invoice_registration_number':
          if (invoiceRegistrationNumberKey) {
            const reportFormData = formWatch('reportForm');
            const fieldValue = reportFormData?.[invoiceRegistrationNumberKey];
            value = typeof fieldValue === 'string' ? fieldValue : undefined;
          } else {
            value = undefined;
          }
          break;
        case 'bank':
          value = formWatch('payee.instantPayee.name');
          break;
        case 'bank_account_type': {
          const accountType = formWatch('payee.instantPayee.accountType');
          value = accountType
            ? getBankAccountTypeLabel(accountType, t)
            : undefined;
          break;
        }
        case 'bank_account_number':
          value = formWatch('payee.instantPayee.number');
          break;
        case 'bank_name_kana':
          value = formWatch('payee.instantPayee.holderNameKana');
          break;
        default:
          return undefined;
      }
      return value;
    },
    [formWatch, t, invoiceRegistrationNumberKey, payee?.name]
  );

  const getInferenceReason = useCallback(
    (key: InferenceTooltipKey): string | undefined => {
      const inference = ocrInferenceResult?.inferences?.[0]?.[key];
      return inference?.source === 'system' ? inference.reason : undefined;
    },
    [ocrInferenceResult]
  );

  return (
    <div className={cx(styles['container'])}>
      <div className={cx(styles['title'])}>{t('application_form_title')}</div>
      {isVisibleInstantPayee && (
        <OcrReflectionApplicationFormSwitcher
          onChange={setIsInstantPayeeOpened}
          value={isInstantPayeeOpened}
        />
      )}

      <div className={cx(styles['form-container'])}>
        {/* Header Row */}
        <ApplicationDetailHeader
          allChecked={allChecked}
          indeterminate={indeterminate}
          onToggleAll={toggleAllChecked}
        />

        {/* 支払先名/Payee Name */}
        {isInstantPayeeOpened !== 'instant' && (
          <ApplicationDetailRow
            title={t('ap_payee')}
            beforeValue={getFormContextValues('payee')}
            afterValue={getTransformedValueAsString('payeeSelected')}
            testIdSuffix='payee-name'
            checked={selectionState.payeeSelected}
            onChange={(checked) => updateSelection('payeeSelected', checked)}
          />
        )}

        {/* TODO: I want to separate components */}
        {isInstantPayeeOpened === 'instant' && (
          <>
            {/* 支払先名 / Instant Payee Name */}
            <ApplicationDetailRow
              title={t('ap_payee_name')}
              beforeValue={getFormContextValues('bank')}
              afterValue={getTransformedValueAsString(
                'instantPayeeNameSelected'
              )}
              testIdSuffix='instant-payee-name'
              checked={selectionState.instantPayeeNameSelected}
              onChange={(checked) =>
                updateSelection('instantPayeeNameSelected', checked)
              }
            />

            {/* 銀行名 / Bank Name */}
            <ApplicationDetailRow
              title={t('bank_name')}
              beforeValue={draftInstantPayeeBankName}
              afterValue={getTransformedValueAsString('bankNameSelected')}
              testIdSuffix='bank-name'
              checked={selectionState.bankNameSelected}
              onChange={(checked) =>
                updateSelection('bankNameSelected', checked)
              }
            />

            {/* 支店名 / Bank Branch Name */}
            <ApplicationDetailRow
              title={t('bank_branch_name')}
              beforeValue={draftInstantPayeeBankBranchName}
              afterValue={getTransformedValueAsString('bankBranchNameSelected')}
              testIdSuffix='bank-branch-name'
              checked={selectionState.bankBranchNameSelected}
              onChange={(checked) =>
                updateSelection('bankBranchNameSelected', checked)
              }
            />

            {/* 口座種別 / Bank Account Type */}
            <ApplicationDetailRow
              title={t('bank_account_type')}
              beforeValue={getFormContextValues('bank_account_type')}
              afterValue={getBankAccountTypeLabel(
                getTransformedValueAsString('bankAccountTypeSelected'),
                t
              )}
              testIdSuffix='bank-account-type'
              checked={selectionState.bankAccountTypeSelected}
              onChange={(checked) =>
                updateSelection('bankAccountTypeSelected', checked)
              }
            />

            {/* 口座番号 / Bank Account Number */}
            <ApplicationDetailRow
              title={t('bank_account_number')}
              beforeValue={getFormContextValues('bank_account_number')}
              afterValue={getTransformedValueAsString(
                'bankAccountNumberSelected'
              )}
              testIdSuffix='bank-account-number'
              checked={selectionState.bankAccountNumberSelected}
              onChange={(checked) =>
                updateSelection('bankAccountNumberSelected', checked)
              }
            />

            {/* 銀行名カナ / Bank Name Kana */}
            <ApplicationDetailRow
              title={t('bank_name_kana')}
              beforeValue={getFormContextValues('bank_name_kana')}
              afterValue={getTransformedValueAsString('bankNameKanaSelected')}
              testIdSuffix='bank-name-kana'
              checked={selectionState.bankNameKanaSelected}
              onChange={(checked) =>
                updateSelection('bankNameKanaSelected', checked)
              }
            />
          </>
        )}

        {/* 費用計上日 / Book Date */}
        <ApplicationDetailRow
          title={t('payment_closing_date_label')}
          beforeValue={getFormContextValues('book_date')}
          afterValue={getTransformedValueAsString('bookDateSelected')}
          testIdSuffix='book-date'
          checked={selectionState.bookDateSelected}
          onChange={(checked) => updateSelection('bookDateSelected', checked)}
          tooltip={getInferenceReason('bookDate')}
        />

        {/* 支払期日 / Due Date  */}
        <ApplicationDetailRow
          title={t('payment_month_label')}
          beforeValue={getFormContextValues('due_date')}
          afterValue={getTransformedValueAsString('dueDateSelected')}
          testIdSuffix='due-date'
          checked={selectionState.dueDateSelected}
          onChange={(checked) => updateSelection('dueDateSelected', checked)}
          tooltip={getInferenceReason('dueDate')}
        />

        {/* 適格請求書発行者番号（条件付き） / Invoice Registration Number (Conditional) */}
        {isHiddenInvoiceRegistrationNumber === false && (
          <ApplicationDetailRow
            title={t('invoice_registration_number')}
            beforeValue={getFormContextValues('invoice_registration_number')}
            afterValue={getTransformedValueAsString(
              'invoiceRegistrationNumberSelected'
            )}
            testIdSuffix='invoice-registration-number'
            checked={selectionState.invoiceRegistrationNumberSelected}
            onChange={(checked) =>
              updateSelection('invoiceRegistrationNumberSelected', checked)
            }
            tooltip={getInferenceReason('invoiceRegistrationNumber')}
          />
        )}
      </div>
    </div>
  );
};
InnerOcrReflectionApplicationForm.displayName =
  'InnerOcrReflectionApplicationForm';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const OcrReflectionApplicationForm: FC<Props> = memo(
  InnerOcrReflectionApplicationForm
);
